import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { detectLocale, i18n, LOCALES, setLocale, type Locale } from './i18n';
import { ensureSession, session } from './session';

/** Seiten mit sprachabhängigem Pfad: /de/anmelden, /en/login usw. */
export type Page = 'home' | 'login' | 'mail' | 'faq' | 'blacklist' | 'removal' | 'imprint';

const SLUGS: Record<Locale, Record<Page, string>> = {
  de: {
    home: '',
    login: 'anmelden',
    mail: 'postfach',
    faq: 'faq',
    blacklist: 'blacklist',
    // Statusseite eines Austragungsantrags (Link mit tracking_uuid)
    removal: 'blacklist/antrag/:id',
    imprint: 'impressum',
  },
  en: {
    home: '',
    login: 'login',
    mail: 'inbox',
    faq: 'faq',
    blacklist: 'blacklist',
    removal: 'blacklist/request/:id',
    imprint: 'imprint',
  },
};

const components = {
  home: () => import('./pages/HomePage.vue'),
  login: () => import('./pages/LoginPage.vue'),
  mail: () => import('./pages/MailPage.vue'),
  faq: () => import('./pages/FaqPage.vue'),
  blacklist: () => import('./pages/BlacklistPage.vue'),
  removal: () => import('./pages/RemovalStatusPage.vue'),
  imprint: () => import('./pages/ImprintPage.vue'),
} satisfies Record<Page, unknown>;

/** Name der Route einer Seite in einer Sprache */
export const routeName = (page: Page, locale: Locale) => `${page}.${locale}`;

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: () => `/${detectLocale()}/` },
  ...LOCALES.flatMap((locale) =>
    (Object.keys(SLUGS[locale]) as Page[]).map(
      (page): RouteRecordRaw => ({
        path: `/${locale}/${SLUGS[locale][page]}`,
        name: routeName(page, locale),
        component: components[page],
        meta: { locale, page },
      }),
    ),
  ),
  { path: '/:pathMatch(.*)*', redirect: () => `/${detectLocale()}/` },
];

declare module 'vue-router' {
  interface RouteMeta {
    locale?: Locale;
    page?: Page;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const { locale, page } = to.meta;
  if (!locale || !page) return true;
  if (i18n.global.locale.value !== locale || document.documentElement.lang !== locale) setLocale(locale);

  await ensureSession();
  // Postfach nur mit Sitzung, Anmeldeseite nur ohne
  if (page === 'mail' && !session.value) return { name: routeName('login', locale) };
  if (page === 'login' && session.value) return { name: routeName('mail', locale) };
  return true;
});

router.afterEach((to) => {
  const page = to.meta.page;
  if (!page) return;
  const t = i18n.global.t;
  document.title = page === 'home' ? `anonmails.de – ${t('meta.home')}` : `${t(`meta.${page}`)} – anonmails.de`;
});
